import React, { useEffect, useRef, useState } from 'react';
import { graphql, Link } from 'gatsby';
import { dropWhile, get, isNil } from 'lodash';
import { getImage } from 'gatsby-plugin-image';
import { Social } from '@/components/Social';
import { CallToAction } from '@/components/CallToAction';
import { faArrowLeftLong } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useScrollPosition from '@/hooks/useScrollPosition';
import { Hero } from '@/components/Hero';
import { ArticleMeta } from '@/components/ArticleMeta';
import Layout, { Main, Aside } from '../../components/Layout';
import { Content } from '../../components/Content';
import { Seo } from '../../components/SEO';
import { Text } from '../../components/Text';
import { StyledArticle } from './styles';
import { ArticleDesktopTemplate } from './DesktopTemplate';
import { ArticleMobileTemplate } from './MobileTemplate';
import { TableOfContent } from './TableOfContent/TableOfContent';
import { BottomContent } from './BottomContent/BottomContent';

const isBrowser = typeof window !== `undefined`;

export type SiblingArticle = {
  excerpt: string;
  title: string;
  image: any;
  slug: string;
};

interface IArticle {
  pageContext: any;
  data: {
    file: {
      childImageSharp: {
        fixed: {
          aspectRatio: string;
          base64: string;
          sizes: any;
          src: string;
          srcSet: string;
        };
      };
    };
    site: {
      siteMetadata: {
        title: string;
        siteUrl: string;
      };
    };
    contentfulArticle: any;
    related: any;
  };
  location: object;
}

const ArticlePage = ({ data, location, pageContext }: IArticle) => {
  const article = get(data, `contentfulArticle`);
  const related = get(data, `related.edges`);
  // const site = get(data, `site`);
  const page = get(data, `articlePage`);
  const { svgTopPath, svgBottomPath, ctaTitle, ctaHookId, ctaIcon, ctaBody } =
    page;
  const {
    templateKey,
    title,
    body,
    slug,
    heroImage,
    metaTitle,
    metaDescription,
    author,
    tags,
    updateDate,
    timeToRead,
    articlesInSeries,
    seriesOrder,
  } = article;
  const content = get(body, `childMarkdownRemark.html`);
  const markdownContent = body?.body;
  const { next, prev } = pageContext;
  const hero = getImage(heroImage);

  const svgExLeft = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" preserveAspectRatio="none" viewBox="0 0 100 1011"><rect fill="#000" x="0" y="0" width="100" height="1011"/></svg>`;
  const svgExRight = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" preserveAspectRatio="none" viewBox="0 0 100 890"><rect fill="#000" x="0" y="0" width="100" height="890"/></svg>`;

  const contentRef = useRef<HTMLDivElement>();
  const { y: scrollPositionY } = isBrowser ? useScrollPosition() : { y: 0 };
  const [tocList, setTocList] = useState<{ offset: number; name: string }[]>(
    [],
  );
  const [currentHeaderInPosY, setCurrentHeaderInPosY] = useState<string>(``);

  useEffect(() => {
    if (!contentRef?.current) {
      return;
    }
    const selected = dropWhile(
      tocList.map(({ offset, name }, idx, array) => {
        const isLast = array.length - 1 === idx;
        if (!isLast) {
          return offset <= scrollPositionY &&
            array[idx + 1].offset >= scrollPositionY
            ? name
            : null;
        }
        return offset <= scrollPositionY ? name : null;
      }),
      isNil,
    )[0];
    setCurrentHeaderInPosY(selected);
  }, [scrollPositionY, contentRef?.current, tocList]);

  useEffect(() => {
    if (!contentRef?.current) {
      return;
    }
    setTimeout(() => {
      const listH1 = contentRef.current.querySelectorAll(`h1, h2, h3`);
      const newList = [...listH1].map((item) => ({
        offset: item.offsetTop + 640, // 700 is the magic number of the height of the hero.
        name: item.id,
      }));
      setTocList(newList);
    }, 300);
  }, [contentRef]);

  const nextArticle = {
    image: getImage(next?.thumbImage),
    slug: next && next?.slug && `/blog/${next.slug}`,
    title: next?.title,
    excerpt: next?.excerpt,
  };
  const prevArticle = {
    image: getImage(prev?.thumbImage),
    slug: prev && prev?.slug && `/blog/${prev.slug}`,
    title: prev?.title,
    excerpt: prev?.excerpt,
  };

  const relatedArticlesToDisplay = related.map(({ node }) => {
    const { thumbImage: nodeThumbImage } = node;
    const articleThumbImage = getImage(nodeThumbImage);
    return {
      title: node.title,
      content: node.excerpt,
      imageUrl: articleThumbImage,
      link: `/blog/${node.slug}`,
      isVideo: false,
      ...node,
    };
  });

  const series =
    articlesInSeries.length > 0 &&
    [
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ...articlesInSeries.map(({ id, ...rest }) => ({
        ...rest,
        current: false,
      })),
      { title, slug, seriesOrder, current: true },
    ].sort((a, b) => a.seriesOrder - b.seriesOrder);

  return (
    <StyledArticle
      maskImgBg={`data:image/svg+xml,${encodeURIComponent(
        svgTopPath?.svg?.content,
      )}`}
      maskImgLeft={`data:image/svg+xml,${encodeURIComponent(svgExLeft)}`}
      maskImgRight={`data:image/svg+xml,${encodeURIComponent(svgExRight)}`}
      ref={contentRef}
    >
      <Layout
        location={location}
        coverImage={hero}
        bottomSvg={svgBottomPath?.svg?.content}
        variant={4}
        hasRightContent
        desktopHeading={
          <div>
            <Link to="/blog/" className="back-button">
              <FontAwesomeIcon icon={faArrowLeftLong} />
              <span className="back-content">Back to blog</span>
            </Link>
            <Content restrictWidth className="hero-container">
              <Text type="title1" noRhythem className="main-title">
                {title}
              </Text>
            </Content>
          </div>
        }
        desktopBody={
          <ArticleDesktopTemplate
            content={content}
            series={series}
            rightChildren={
              <TableOfContent
                className="article-content__right"
                rawContent={markdownContent}
                slug={slug}
                headerPosition={currentHeaderInPosY}
              />
            }
            topChildren={
              <ArticleMeta
                author={author}
                authorBig
                date={updateDate}
                timeToRead={timeToRead}
                tags={tags}
                imageSize="xxl"
                className="article-meta"
                allTags
              />
            }
          >
            <BottomContent
              isPreview="false"
              title={title}
              slug={slug}
              nextArticle={nextArticle}
              prevArticle={prevArticle}
              metaDescription={metaDescription}
              relatedArticlesToDisplay={relatedArticlesToDisplay}
            />
          </ArticleDesktopTemplate>
        }
        desktopCta={
          <Content>
            <CallToAction
              hookId={ctaHookId}
              icon={ctaIcon}
              title={ctaTitle}
              body={ctaBody.ctaBody}
              maxWidth="100%"
              className="cta"
            />
            <Social />
          </Content>
        }
      >
        <Seo
          title={title}
          metaTitle={metaTitle}
          metaDesc={metaDescription}
          cover={hero}
          slug={slug}
          isArticlePage
          templateKey={templateKey}
        />
        <Hero imageSrc={hero}>
          <div>
            <Link to="/blog/" className="back-button">
              <FontAwesomeIcon icon={faArrowLeftLong} />
              <span className="back-content">Back to blog</span>
            </Link>
            <Content restrictWidth className="hero-container">
              <Text type="title1" noRhythem className="main-title">
                {title}
              </Text>
            </Content>
          </div>
        </Hero>
        <ArticleMeta
          author={author}
          authorBig
          date={updateDate}
          timeToRead={timeToRead}
          tags={tags}
          imageSize="xxl"
          className="article-meta"
          allTags
        />
        <div className="article-layout-inner">
          <Main showScrrollBackground={false}>
            <ArticleMobileTemplate content={content} series={series}>
              <BottomContent
                isPreview="false"
                title={title}
                slug={slug}
                nextArticle={nextArticle}
                prevArticle={prevArticle}
                metaDescription={metaDescription}
                relatedArticlesToDisplay={relatedArticlesToDisplay}
                classPrefix="mobile"
                nextPrevNoImage
              />
            </ArticleMobileTemplate>
          </Main>
          <Aside disableBackground={false}>
            <TableOfContent
              container
              rawContent={markdownContent}
              slug={slug}
              headerPosition={currentHeaderInPosY}
            />
          </Aside>
        </div>
      </Layout>
    </StyledArticle>
  );
};

export default ArticlePage;

export const articleQuerys = graphql`
  query ArticleBySlug($slug: String!) {
    site {
      siteMetadata {
        title
        siteUrl
      }
    }
    contentfulArticle(slug: { eq: $slug }) {
      templateKey
      title
      slug
      tags
      updateDate(formatString: "MMMM Do, YYYY")
      body {
        childMarkdownRemark {
          html
        }
        body
      }
      author {
        slug
        name
        image {
          gatsbyImageData(
            width: 105
            height: 105
            quality: 85
            placeholder: BLURRED
          )
        }
      }
      heroImage {
        gatsbyImageData(layout: FULL_WIDTH, quality: 85, placeholder: BLURRED)
      }
      metaTitle
      metaDescription
      timeToRead
      articlesInSeries {
        id
        title
        slug
        seriesOrder
      }
      seriesOrder
    }
    related: allContentfulArticle(
      sort: { fields: [publishDate], order: DESC }
      limit: 6
    ) {
      edges {
        node {
          templateKey
          title
          slug
          id
          timeToRead
          tags
          createDate(formatString: "MMMM Do, YYYY")
          publishDate(formatString: "MMMM Do, YYYY")
          thumbImage {
            description
            gatsbyImageData(width: 344, height: 180, quality: 100)
          }
          excerpt
          author {
            name
            slug
            image {
              gatsbyImageData(
                width: 40
                height: 40
                quality: 100
                placeholder: BLURRED
              )
            }
          }
        }
      }
    }
    articlePage: contentfulPage(slug: { eq: "article" }) {
      metaTitle
      metaDescription
      svgTopPath {
        svg {
          content
        }
      }
      svgBottomPath {
        svg {
          content
        }
      }
      ctaTitle
      ctaHookId
      ctaIcon {
        gatsbyImageData(width: 48, quality: 100, placeholder: BLURRED)
      }
      ctaBody {
        ctaBody
      }
    }
  }
`;
